/**
 * Created by james on 4/30/14.
 */
"use strict";

// ---------------------------------------------------
// Load Super Pseudo Class

var SoaModel = require('bunsen/models/SoaModel');

// ---------------------------------------------------
// Define Module

/**
 * @class ShipmentQuote
 */
var ShipmentQuote = module.exports = SoaModel.extend();

// ---------------------------------------------------
// Dependencies

var _ = require('underscore');
var ServiceMethod = require('bunsen/models/logistics/ServiceMethod');
var ShippingCosts = require('bunsen/collections/logistics/ShippingCosts');
var ShippingCost = require('bunsen/models/logistics/ShippingCost');
var Backbone = require('dibs-backbone');
var endpoints = require('dibs-endpoints');
var Party = require('bunsen/models/Party');
var Period = require('bunsen/models/Period');
var periodMixin = require('bunsen/models/mixins/periodMixin');
var SV = require('server-vars');
var Carrier = require('bunsen/models/logistics/Carrier');
var FinancialAmount = require('bunsen/models/FinancialAmount');
var financialAmountMixin = require('bunsen/models/mixins/financialAmountMixin');
var Region = require('bunsen/models/logistics/Region');
var assert = require('bunsen/helpers/assert');
var stringNumHelper = require('bunsen/helpers/stringNumHelper');

// ---------------------------------------------------
// Define Module Static Properties

_.extend(ShipmentQuote, {
    /** @type {string} */
    TYPE_SELLER_PREFERENCES : 'SELLER_PREFERENCES',
    /** @type {string} */
    TYPE_INVENTORY_POSTING : 'INVENTORY_POSTING',
    /** @type {string} */
    TYPE_TRANSACTION : 'TRANSACTION',
    /** @type {string} */
    PRICING_TYPE_ACCOUNTING: 'ACCOUNTING',
    /** @type {string} */
    PRICING_TYPE_COMMERCIAL: 'COMMERCIAL',
    /** @type {string} */
    SERVICE_ERROR_RESOLVE_IP: 'UNABLE_TO_RESOLVE_COUNTRY_FROM_IP_ADDRESS',
    /** @type {object} */
    validationRules: {
        /** @type {string} */
        CANNOT_BE_GREATER_THAN: 'CANNOT_BE_GREATER_THAN',
        /** @type {string} */
        CANNOT_BE_LESS_THAN: 'CANNOT_BE_LESS_THAN'
    },
    /**
     * @returns {string[]}
     */
    getPreQuoteTypes : function () {
        return [
            ShipmentQuote.TYPE_SELLER_PREFERENCES,
            ShipmentQuote.TYPE_INVENTORY_POSTING
        ];
    },
    /**
     * @param {object} attrs
     * @returns {ShipmentQuote}
     */
    createLocalPickupQuote: function (attrs) {
        attrs = attrs ? _.clone(attrs) : {};
        attrs.serviceMethod = _.extend({
            serviceMethodType: ServiceMethod.METHOD_LOCAL_PICKUP
        }, attrs.serviceMethod);
        return new ShipmentQuote(attrs);
    }
});

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(ShipmentQuote.prototype, {
    urlRoot: endpoints.shipmentQuote(),
    url: function () {
        var urlRoot = this.urlRoot;
        return this.id ? (urlRoot + '/' + this.id) : urlRoot;
    },
    defaults: {
        apiType: 'shipmentQuote',
        serviceMethod: {},
        author: {},
        billTo: {},
        costs: []
    },
    relations: SoaModel.createRelations([
        {
            key: 'author',
            type: Backbone.One,
            relatedModel: Party
        },
        {
            key: 'billTo',
            type: Backbone.One,
            relatedModel: Party
        },
        {
            key: 'handlingTime',
            type: Backbone.One,
            relatedModel: Period
        },
        {
            key: 'serviceMethod',
            type: Backbone.One,
            relatedModel: ServiceMethod
        },
        {
            key: 'costs',
            type: Backbone.Many,
            collectionType: ShippingCosts
        },
        {
            type : Backbone.One,
            key : 'carrier',
            relatedModel : Carrier
        },
        {
            type: Backbone.One,
            key: 'subTotalAmount',
            relatedModel: FinancialAmount
        },
        {
            type: Backbone.One,
            key: 'totalAmount',
            relatedModel: FinancialAmount
        }
    ]),
    initialize: function (attrs, opts) {
        opts = opts || {};
        this.serviceMethodRequiredFields = opts.serviceMethodRequiredFields || [
            'serviceMethodType', 'serviceType', 'windowMin.numberOfUnits', 'windowMax.numberOfUnits'
        ];
        opts.validateRequiredFields = _.isBoolean(opts.validateRequiredFields) ?
                opts.validateRequiredFields : this.validateRequiredFields;

        ShipmentQuote.__super__.initialize.call(this, attrs, opts);
    },
    /**
     * @returns {boolean}
     */
    is1stdibsQuote: function () {
        return this.is1stdibsBilledQuote() && !this.isAccountingQuote();
    },
    /**
     * @returns {boolean}
     */
    is1stdibsAuthoredQuote: function () {
        var author = this.get('author');
        return author && author.is1stdibs();
    },
    /**
     * @returns {boolean}
     */
    isDealerAuthoredQuote: function () {
        var author = this.get('author');
        return author && author.isDealer() && !this.isLocalPickup();
    },
    /**
     * @returns {boolean}
     */
    is1stdibsBilledQuote: function () {
        var billToParty = this.get('billTo');
        return billToParty && billToParty.is1stdibs();
    },
    /**
     * @returns {boolean}
     */
    isDealerBilledQuote: function () {
        var billToParty = this.get('billTo');
        return billToParty && billToParty.isDealer();
    },

    /**
     * Returns true if the dealer is shipping an item
     * with an auto-generated quote that is subsized
     * by 1stdibs. Right now the test for that is simply
     * that the quote is a 1stdibs Parcel quote and also
     * a prequote. Prequotes will not be generated by
     * 1stdibs for this program.
     * @returns {boolean}
     */
    is1stdibsSubsidizedDealerQuote: function () {
        return this.is1stdibsParcelQuote() && this.isPreQuote();
    },

    /**
     * @returns {boolean}
     */
    is1stdibsParcelQuote: function () {
        return this.is1stdibsQuote() && this.isParcelServiceType();
    },

    /**
     * @returns {boolean}
     */
    is1stdibsWhiteGloveQuote: function () {
        return this.is1stdibsQuote() && this.isWhiteGloveServiceType();
    },

    /**
     * @returns {boolean}
     */
    isParcelServiceType: function () {
        return this.get('serviceMethod').isParcel();
    },

    /**
     * @returns {boolean}
     */
    isWhiteGloveServiceType: function () {
        return this.get('serviceMethod').isWhiteGlove();
    },

    /**
     * @returns {boolean}
     */
    hasPreQuoteType: function () {
        return _.contains(ShipmentQuote.getPreQuoteTypes(), this.get('type'));
    },
    /**
     * @returns {boolean}
     */
    isPreQuote: function () {
        return this.hasPreQuoteType() && !this.isLocalPickup();
    },
    /**
     * @returns {boolean}
     */
    isCommercialQuote: function () {
        return this.get('pricingType') === ShipmentQuote.PRICING_TYPE_COMMERCIAL;
    },
    /**
     * @returns {boolean}
     */
    isAccountingQuote: function () {
        return this.get('pricingType') === ShipmentQuote.PRICING_TYPE_ACCOUNTING;
    },
    /**
     * @returns {boolean}
     */
    is1stdibsPreQuote: function () {
        return this.is1stdibsQuote() && this.isPreQuote();
    },
    /**
     * @returns {boolean}
     */
    isDealerPreQuote: function () {
        return this.isDealerBilledQuote() && this.isPreQuote();
    },
    /**
     * @returns {boolean}
     */
    isComplimentaryShipping: function () {
        return this.get('complimentaryShipping') === 'Y';
    },
    /**
     * @returns {boolean}
     */
    isLocalPickup: function () {
        var serviceMethod = this.get('serviceMethod');
        return serviceMethod && serviceMethod.isLocalPickup();
    },
    getRole: function () {
        return this.get('author.role');
    },
    /**
     * @returns {boolean}
     */
    regionIsWorld: function () {
        return Region.regionCodeIsWorld(this.get('serviceMethod.region.code'));
    },
    /**
     * Returns an array of attribute keys for missing
     * fields, and null if there aren't any
     * @returns {string[]}
     */
    validate: function () {
        var missingFields;
        if (this.validateRequiredFields) {
            missingFields = this.missingRequiredFields();
            return missingFields.length ? missingFields : null;
        }
        return null;
    },
    /**
     * @returns {string}
     */
    describeDuration: function () {
        var handlingTimeDays;
        var min;
        var max;

        //TRANACT-2371 - Hide duration for local pickup
        if (this.isLocalPickup()) {
            return '';
        }
        handlingTimeDays = this.getHandlingTimeDays();
        min = this.getMinShippingDays() + (handlingTimeDays || 0);
        max = this.getMaxShippingDays() + (handlingTimeDays || 0);
        return this._describeRange(min, max);
    },

    /**
     * @returns {string}
     */
    describeShippingWindow: function () {
        return this._describeRange(this.getMinShippingDays(), this.getMaxShippingDays());
    },

    /**
     * @returns {string}
     */
    getBuyerServiceMethodDisplayValue: function () {
        var quoteAuthorRole = this.get('author.role');
        var type = this.get('serviceMethod.serviceType');
        var methodType = this.get('serviceMethod.serviceMethodType');
        var names = SV.get('serviceMethodNames.' + quoteAuthorRole);

        if (!type || !methodType || !names) {
            return '';
        }
        return names[type][methodType].name;
    },

    /**
     * @returns {number}
     */
    getMaxShippingDays: function () {
        return this.getPeriodInDays('serviceMethod.windowMax');
    },

    /**
     * @returns {number}
     */
    getMinShippingDays : function () {
        return this.getPeriodInDays('serviceMethod.windowMin');
    },

    /**
     * @returns {number}
     */
    getHandlingTimeDays : function () {
        return this.getPeriodInDays('handlingTime');
    },

    /**
     * @returns {string}
     */
    getDisplayHandlingTimeDays: function () {
        return this.getPeriodDisplayVal('handlingTime');
    },

    /**
     * @returns {string}
     */
    _describeRange: function (minDays, maxDays) {
        var unit = "day";
        if (!maxDays) {
            return "";
        }
        if (minDays >= 10) {
            minDays = Math.floor(minDays / 7);
            maxDays = Math.ceil(maxDays / 7);
            unit = "week";
        }
        if (minDays === maxDays) {
            return [minDays, ' ', unit, minDays === 1 ? '' : 's'].join('');
        }
        return [minDays, ' to ', maxDays, ' ', unit, 's'].join('');
    },


    /**
     * Returns the shipping price to be displayed from totalAmount.  If it is complimentary, returns null
     * @param  {String} currency - Optional - 3 character currency code.  Defaults to currency attribute of totalAmount
     * @return {String|null} - null if complimentary.  Otherwise formatted price.
     */
    getDisplayPrice: function (currency) {
        if (this.isComplimentaryShipping()) {
            return null;
        }
        return this.get('totalAmount').getDisplayPrice(currency);
    },

    /**
     * Returns an array with the keys for
     * any missing required fields
     * @param {string} [requiredCostType]
     * @returns {string[]}
     */
    missingRequiredFields: function (requiredCostType) {
        var serviceMethod = this.get('serviceMethod');
        var missingFields = [];
        var field;
        var val;
        var subTotal;
        var i = 0;
        for (i; i < this.serviceMethodRequiredFields.length; i++) {
            field = this.serviceMethodRequiredFields[i];
            val = serviceMethod.get(field);
            if (val === undefined || val === '' || val === null || (typeof val === 'number' && val < 0)) {
                missingFields.push(field);
            }
        }
        if (!this.get('serviceMethod.region.id') && !this.get('serviceMethod.region.regionName')) {
            missingFields.push('region');
        }
        if (requiredCostType) {
            subTotal = parseFloat(this.getSubTotalNumForCostType(requiredCostType));
            // There must be a price, or this must be a free shipping quote
            if ((!subTotal || subTotal < 0) && !this.isComplimentaryShipping()) {
                missingFields.push('subTotalAmount');
            }
        }
        return missingFields;
    },

    /**
     * @returns {object}
     */
    getAmountCurrencies: function () {
        var amountCurrencies = this.get('totalAmount');
        var ret = null;

        //to handle case where complimentaryShipping == 'N' but prices are 0...
        if (amountCurrencies && amountCurrencies.getAmountIn('USD')) {
            ret = amountCurrencies.get('convertedAmounts').toJSON();
        }

        return ret;
    },

    /**
     * @returns {FinancialAmount}
     */
    getSubTotalAmount: function () {
        return this.get('subTotalAmount');
    },

    /**
     * @returns {ShippingCosts}
     */
    getShippingCosts: function () {
        return this.get('costs');
    },

    /**
     * @param {number} subTotal
     * @param {string} costType
     * @returns {ShipmentQuote}
     */
    setSubTotalForCostType: function (subTotal, costType) {
        var costs = this._ensureShippingCosts();
        costs.setSubTotalForType(costType, subTotal);
        return this;
    },

    /**
     * Returns true if any of the quote's costs
     * have a subtotal greater than zero.
     * @returns {boolean}
     */
    hasCostGreaterThanZero: function () {
        var costs = this.getShippingCosts();
        if (!costs) {
            return false;
        }
        return costs.any(function (cost) {
            var val = stringNumHelper.safeConvertNumberVal(cost.getSubTotalNum());
            return val !== null && val > 0;
        });
    },

    /**
     * Get a ShippingCost for a costType from
     * the quotes shipping cost collection. If
     * that cost doesn't exist yet, then it
     * will be created.
     * @param {string} costType
     * @returns {ShippingCost}
     */
    ensureShippingCostForType: function (costType) {
        var costs = this._ensureShippingCosts();
        var cost = costs.findByCostType(costType);
        if (cost) {
            return cost;
        }
        costs.setSubTotalForType(costType, null);
        return costs.findByCostType(costType);
    },

    /**
     * @param {number} subTotal
     * @returns {ShipmentQuote}
     */
    setUnknownCostSubTotal: function (subTotal) {
        return this.setSubTotalForCostType(subTotal, ShippingCost.COST_TYPE_UNKNOWN);
    },

    /**
     * @param {string} costType
     * @returns {ShippingCost}
     */
    findShippingCostForType : function (costType) {
        var costs = this.getShippingCosts();
        if (!costs) {
            return null;
        }
        return costs.findByCostType(costType);
    },

    /**
     * @param {string} costType
     * @returns {FinancialAmount}
     */
    getSubTotalAmountForCostType: function (costType) {
        var costs = this.getShippingCosts();
        if (!costs) {
            return null;
        }
        return costs.getSubTotalAmountForType(costType);
    },

    /**
     * gets the numeric subtotal amount for the given costType in the given
     * currency, defaulting to USD if no currency is supplied
     * @param {string} costType
     * @param {string} [currency]
     * @returns {number}
     */
    getSubTotalAmountForCostTypeIn: function (costType, currency) {
        var subTotalAmount;
        currency = currency || 'USD';
        subTotalAmount = this.getSubTotalAmountForCostType(costType);
        return subTotalAmount ? subTotalAmount.getAmountIn(currency) : null;
    },

    /**
     * @param {string} costType
     * @returns {number}
     */
    getSubTotalNumForCostType: function (costType) {
        var costs = this.getShippingCosts();
        if (!costs) {
            return null;
        }
        return costs.getSubTotalNumForType(costType);
    },

    /**
     * @param {string} costType
     * @param {string} currency
     * @returns {number}
     */
    getSubTotalInCurrForCostType: function (costType, currency) {
        var subTotalAmount = this.getSubTotalAmountForCostType(costType);
        return subTotalAmount ? subTotalAmount.getAmountIn(currency) : null;
    },

    /**
     * Return the currency of the first shipment cost
     * @returns {string}
     */
    getFirstCostCurrency: function () {
        var costs = this.getShippingCosts();
        return (costs && costs.length) ? costs.first().getCurrency() : null;
    },

    /**
     * @param {string} currency
     * @returns {ShipmentQuote}
     */
    setDefaultCurrency: function (currency) {
        var costs = this._ensureShippingCosts();
        costs.setDefaultCurrency(currency);
        return this;
    },

    /**
     * @param {string} costType
     * @param {Carrier} carrier
     * @returns {ShipmentQuote}
     */
    setCarrierForCostType: function (costType, carrier) {
        var costs = this._ensureShippingCosts();
        assert(carrier instanceof Carrier, 'carrier must be an instance of Carrier');
        costs.setBillToForType(costType, Party.ROLE_LSP, carrier.get('id'));
        return this;
    },

    /**
     * @param {string} costType
     * @returns {number|string}
     */
    getBillToIdForCostType: function (costType) {
        var cost = this.findShippingCostForType(costType);
        if (!cost) {
            return null;
        }
        return cost.getBillToId();
    },

    /**
     * @param {string} costType
     * @returns {string}
     */
    getCarrierNameForCostType: function (costType) {
        var costs = this.getShippingCosts();
        return costs ? costs.findBillToCarrierNameForType(costType) : null;
    },

    /**
     * @param {string} costType
     * @returns {string}
     */
    getCarrierCodeForCostType: function (costType) {
        var costs = this.getShippingCosts();
        return costs ? costs.findBillToCarrierCodeForType(costType) : null;
    },

    /**
     * Returns the 'UNKNOWN' shipping cost if present, otherwise
     * returns the 'TRANSIT' shipping cost if present, otherwise
     * returns null.
     * @returns {ShippingCost}
     */
    findDefaultShippingCost : function () {
        var costs = this.getShippingCosts();
        return costs ? costs.findDefaultCost() : null;
    },

    /**
     * @returns {string}
     */
    findDefaultCostCarrierName: function () {
        var cost = this.findDefaultShippingCost();
        return (cost && cost.billToIsLSP()) ? cost.findBillToCarrierName() : null;
    },

    /**
     * @param {string} costType
     * @returns {boolean}
     */
    carrierIsOtherForCostType: function (costType) {
        var cost = this.findShippingCostForType(costType);
        return cost ? cost.billToCarrierIsOther() : null;
    },

    /**
     * Returns true only if there is
     * @returns {boolean}
     */
    defaultCostCarrierIsOther: function () {
        var cost = this.findDefaultShippingCost();
        return cost ? cost.billToCarrierIsOther() : null;
    },

    /**
     * @returns {string|number}
     */
    getDefaultCostCarrierId : function () {
        var cost = this.findDefaultShippingCost();
        return (cost && cost.billToIsLSP()) ? cost.getBillToId() : null;
    },

    /**
     * @returns {Deferred.promise}
     */
    computeSubTotal: function () {
        var self = this;
        var oldUrl = self.url;
        var prom;
        self.url = endpoints.computeShipmentQuoteSubTotal();
        prom = self.sync('create', self);
        prom.done(function (data) {
            var parsedData = self.parse(data);
            if (!self.getSubTotalAmount()) {
                self.set('subTotalAmount', {});
            }
            if (!self.get('totalAmount')) {
                self.set('totalAmount', {});
            }
            self.set('subTotalAmount.amount', parsedData.subTotalAmount.amount);
            self.set('subTotalAmount.convertedAmounts', parsedData.subTotalAmount.convertedAmounts);
            self.set('totalAmount.amount', parsedData.totalAmount.amount);
            self.set('totalAmount.convertedAmounts', parsedData.totalAmount.convertedAmounts);
        });
        self.url = oldUrl;
        return prom;
    },

    _ensureShippingCosts: function () {
        var costs = this.getShippingCosts();
        if (costs) {
            return costs;
        }
        this.set('costs', []);
        return this.getShippingCosts();
    }
}, periodMixin, financialAmountMixin);



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/logistics/ShipmentQuote.js
// module id = 74
// module chunks = 64